import {
  Badge,
  Box,
  Button,
  Divider,
  Flex,
  HStack,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  Text,
  VStack,
} from '@chakra-ui/react'
import {
  DEFAULT_TIME_LOG_FIELD_LABELS,
  DeliveryTimeThreshold,
  EmThreshold,
  FieldMap,
  FieldTypes,
  IntrapartumThreshold,
  NumberField,
  TextField,
  TIME_LOG_FIELD_ORDER,
} from '@hb/shared'
import { useCallback, useState } from 'react'
import { DeleteButton } from '../../../Buttons'
import { GenericEditModal } from '../../../DataView/GenericEditModal'
import { Expandable } from '../../../Expandable'
import { Editable } from '../../../forms/Input'
import { TimeLogConfigAdminProvider, useTimeLogConfigAdminContext } from './context'

// ─── Field definitions for Add Threshold modal ───────────────────────────────

const cptCodeField: TextField = {
  type: FieldTypes.TEXT,
  placeholder: 'CPT Code (e.g. 99214)',
}

const thresholdLabelField: TextField = {
  type: FieldTypes.TEXT,
  placeholder: 'Label (e.g. Moderate)',
}

const minMinutesField: NumberField = {
  type: FieldTypes.NUMBER,
  placeholder: 'Min minutes',
  min: 0,
  max: 480,
}

const maxMinutesField: NumberField = {
  type: FieldTypes.NUMBER,
  placeholder: 'Max minutes',
  min: 0,
  max: 480,
}

const addThresholdField: FieldMap = {
  name: 'Add E&M Threshold',
  children: {
    cptCode: cptCodeField,
    label: thresholdLabelField,
    minMinutes: minMinutesField,
    maxMinutes: maxMinutesField,
  },
}

const labelTextField: TextField = {
  type: FieldTypes.TEXT,
  placeholder: 'Label',
}

// ─── Field labels section ─────────────────────────────────────────────────────

const FieldLabelsSection = () => {
  const { data, handleUpdateFieldLabel, handleResetFieldLabel } = useTimeLogConfigAdminContext()

  return (
    <Expandable
      initExpanded
      header={() => (
        <Text
          py={2}
          px={4}
          fontSize="lg"
          fontWeight={600}
          color="gray.700"
          _dark={{ color: 'gray.200' }}>
          Time Category Labels
        </Text>
      )}
      headerProps={{ bg: 'gray.50', _dark: { bg: 'gray.800', borderColor: 'gray.700' } }}
      bg="white"
      _dark={{ bg: 'gray.900' }}>
      <VStack spacing={0} align="stretch" px={3} py={2}>
        {TIME_LOG_FIELD_ORDER.map((key, i) => {
          const currentLabel = data?.fieldLabels?.[key]
          const isCustom = !!currentLabel && currentLabel !== DEFAULT_TIME_LOG_FIELD_LABELS[key]
          return (
            <Box key={key}>
              {i > 0 && <Divider borderColor="gray.200" _dark={{ borderColor: 'gray.600' }} />}
              <Flex align="center" gap={3} py={1.5}>
                <Badge textAlign="center" minW="150px" letterSpacing="1px" colorScheme="green">
                  {key.replace('Minutes', '')}
                </Badge>
                <Box flex={1} minW={0}>
                  <Editable
                    field={labelTextField}
                    value={currentLabel ?? DEFAULT_TIME_LOG_FIELD_LABELS[key]}
                    onSubmit={v => handleUpdateFieldLabel(key, v)}
                    dataCellProps={{ fontSize: 'sm', py: 0 }}
                  />
                </Box>
                {isCustom && (
                  <Button
                    size="xs"
                    variant="ghost"
                    colorScheme="gray"
                    onClick={() => handleResetFieldLabel(key)}>
                    Reset
                  </Button>
                )}
              </Flex>
            </Box>
          )
        })}
      </VStack>
    </Expandable>
  )
}

// ─── Threshold row ────────────────────────────────────────────────────────────

const ThresholdRow = ({ threshold, index }: { threshold: EmThreshold; index: number }) => {
  const { handleUpdateThreshold, handleDeleteThreshold } = useTimeLogConfigAdminContext()

  const handleMinChange = useCallback(
    (val: string) => {
      const n = Number(val)
      if (!isNaN(n)) handleUpdateThreshold(index, { ...threshold, minMinutes: n })
    },
    [index, threshold, handleUpdateThreshold],
  )

  const handleMaxChange = useCallback(
    (val: string) => {
      const n = Number(val)
      if (!isNaN(n)) handleUpdateThreshold(index, { ...threshold, maxMinutes: n })
    },
    [index, threshold, handleUpdateThreshold],
  )

  return (
    <Flex align="center" gap={3} py={1.5} w="100%">
      <Box minW="90px">
        <Editable
          field={cptCodeField}
          value={threshold.cptCode}
          onSubmit={v => handleUpdateThreshold(index, { ...threshold, cptCode: v })}
          dataCellProps={{ fontSize: 'sm', fontWeight: 600, py: 0 }}
        />
      </Box>
      <Box flex={1} minW={0}>
        <Editable
          field={thresholdLabelField}
          value={threshold.label}
          onSubmit={v => handleUpdateThreshold(index, { ...threshold, label: v })}
          dataCellProps={{ fontSize: 'sm', py: 0 }}
        />
      </Box>
      <HStack spacing={1} flexShrink={0}>
        <NumberInput
          size="xs"
          w="72px"
          min={0}
          max={480}
          value={threshold.minMinutes}
          onChange={handleMinChange}>
          <NumberInputField borderRadius={4} />
          <NumberInputStepper>
            <NumberIncrementStepper />
            <NumberDecrementStepper />
          </NumberInputStepper>
        </NumberInput>
        <Text fontSize="xs" color="gray.500">
          –
        </Text>
        <NumberInput
          size="xs"
          w="72px"
          min={0}
          max={480}
          value={threshold.maxMinutes}
          onChange={handleMaxChange}>
          <NumberInputField borderRadius={4} />
          <NumberInputStepper>
            <NumberIncrementStepper />
            <NumberDecrementStepper />
          </NumberInputStepper>
        </NumberInput>
        <Text fontSize="xs" color="gray.500" whiteSpace="nowrap">
          min
        </Text>
      </HStack>
      <DeleteButton itemName={threshold.cptCode} onDelete={() => handleDeleteThreshold(index)} />
    </Flex>
  )
}

// ─── E&M thresholds section ───────────────────────────────────────────────────

const EmThresholdsSection = () => {
  const { data, handleAddThreshold, loading } = useTimeLogConfigAdminContext()
  const [addOpen, setAddOpen] = useState(false)

  const thresholds = (data?.emThresholds ?? []).slice().sort((a, b) => a.minMinutes - b.minMinutes)

  return (
    <>
      <Expandable
        initExpanded
        header={() => (
          <Flex align="center" flex={1} minWidth={0}>
            <Text
              py={2}
              px={4}
              fontSize="lg"
              fontWeight={600}
              color="gray.700"
              _dark={{ color: 'gray.200' }}
              flex={1}>
              E&amp;M Time Thresholds
            </Text>
            <Button
              size="sm"
              colorScheme="green"
              mr={2}
              isLoading={loading}
              onClick={e => {
                e.stopPropagation()
                setAddOpen(true)
              }}>
              + Add Threshold
            </Button>
          </Flex>
        )}
        headerProps={{ bg: 'gray.50', _dark: { bg: 'gray.800', borderColor: 'gray.700' } }}
        bg="white"
        _dark={{ bg: 'gray.900' }}>
        <Box px={3} py={2}>
          <Flex px={1} pb={1}>
            <Text fontSize="xs" color="gray.500" fontWeight={600} minW="90px">
              CPT Code
            </Text>
            <Text fontSize="xs" color="gray.500" fontWeight={600} flex={1}>
              Label
            </Text>
            <Text fontSize="xs" color="gray.500" fontWeight={600} minW="170px" textAlign="center">
              Minutes (min – max)
            </Text>
          </Flex>
          <Divider borderColor="gray.300" _dark={{ borderColor: 'gray.600' }} mb={1} />
          <VStack spacing={0} align="stretch">
            {thresholds.map((threshold, i) => (
              <Box key={`${threshold.cptCode}-${i}`}>
                {i > 0 && <Divider borderColor="gray.200" _dark={{ borderColor: 'gray.600' }} />}
                <ThresholdRow threshold={threshold} index={i} />
              </Box>
            ))}
            {thresholds.length === 0 && (
              <Text fontSize="sm" color="gray.400" fontStyle="italic" py={2}>
                No thresholds configured — click + Add Threshold.
              </Text>
            )}
          </VStack>
        </Box>
      </Expandable>

      <GenericEditModal<EmThreshold>
        field={addThresholdField}
        isOpen={addOpen}
        onClose={() => setAddOpen(false)}
        onSubmit={(d, _onProgress) => handleAddThreshold(d)}
        closeOnSubmit
      />
    </>
  )
}

// ─── §4.3: Intrapartum thresholds section ────────────────────────────────────

const addIntrapartumThresholdField: FieldMap = {
  name: 'Add Intrapartum Threshold',
  children: {
    cptCode: cptCodeField,
    label: thresholdLabelField,
    minMinutes: minMinutesField,
    maxMinutes: maxMinutesField,
  },
}

const IntrapartumThresholdRow = ({
  threshold,
  index,
}: {
  threshold: IntrapartumThreshold
  index: number
}) => {
  const { handleUpdateIntrapartumThreshold, handleDeleteIntrapartumThreshold } =
    useTimeLogConfigAdminContext()

  return (
    <Flex align="center" gap={3} py={1.5} w="100%">
      <Box minW="90px">
        <Editable
          field={cptCodeField}
          value={threshold.cptCode}
          onSubmit={v => handleUpdateIntrapartumThreshold(index, { ...threshold, cptCode: v })}
          dataCellProps={{ fontSize: 'sm', fontWeight: 600, py: 0 }}
        />
      </Box>
      <Box flex={1} minW={0}>
        <Editable
          field={thresholdLabelField}
          value={threshold.label}
          onSubmit={v => handleUpdateIntrapartumThreshold(index, { ...threshold, label: v })}
          dataCellProps={{ fontSize: 'sm', py: 0 }}
        />
      </Box>
      <HStack spacing={1} flexShrink={0}>
        <NumberInput
          size="xs"
          w="72px"
          min={0}
          max={9999}
          value={threshold.minMinutes}
          onChange={val => {
            const n = Number(val)
            if (!isNaN(n)) handleUpdateIntrapartumThreshold(index, { ...threshold, minMinutes: n })
          }}>
          <NumberInputField borderRadius={4} />
          <NumberInputStepper>
            <NumberIncrementStepper />
            <NumberDecrementStepper />
          </NumberInputStepper>
        </NumberInput>
        <Text fontSize="xs" color="gray.500">
          –
        </Text>
        <NumberInput
          size="xs"
          w="72px"
          min={0}
          max={9999}
          value={threshold.maxMinutes}
          onChange={val => {
            const n = Number(val)
            if (!isNaN(n)) handleUpdateIntrapartumThreshold(index, { ...threshold, maxMinutes: n })
          }}>
          <NumberInputField borderRadius={4} />
          <NumberInputStepper>
            <NumberIncrementStepper />
            <NumberDecrementStepper />
          </NumberInputStepper>
        </NumberInput>
        <Text fontSize="xs" color="gray.500" whiteSpace="nowrap">
          min
        </Text>
      </HStack>
      <DeleteButton
        itemName={threshold.cptCode}
        onDelete={() => handleDeleteIntrapartumThreshold(index)}
      />
    </Flex>
  )
}

const IntrapartumThresholdsSection = () => {
  const { data, handleAddIntrapartumThreshold, loading } = useTimeLogConfigAdminContext()
  const [addOpen, setAddOpen] = useState(false)

  const thresholds = (data?.intrapartumThresholds ?? [])
    .slice()
    .sort((a, b) => a.minMinutes - b.minMinutes)

  return (
    <>
      <Expandable
        initExpanded
        header={() => (
          <Flex align="center" flex={1} minWidth={0}>
            <Flex direction="column" flex={1} px={4} py={2}>
              <Text fontSize="lg" fontWeight={600} color="gray.700" _dark={{ color: 'gray.200' }}>
                Intrapartum Time Thresholds
              </Text>
              <Text fontSize="xs" color="gray.500">
                AMA L&amp;D code revisions expected September 2026 — update these without a code
                deploy.
              </Text>
            </Flex>
            <Button
              size="sm"
              colorScheme="green"
              mr={2}
              isLoading={loading}
              onClick={e => {
                e.stopPropagation()
                setAddOpen(true)
              }}>
              + Add Threshold
            </Button>
          </Flex>
        )}
        headerProps={{ bg: 'gray.50', _dark: { bg: 'gray.800', borderColor: 'gray.700' } }}
        bg="white"
        _dark={{ bg: 'gray.900' }}>
        <Box px={3} py={2}>
          <Flex px={1} pb={1}>
            <Text fontSize="xs" color="gray.500" fontWeight={600} minW="90px">
              CPT Code
            </Text>
            <Text fontSize="xs" color="gray.500" fontWeight={600} flex={1}>
              Label
            </Text>
            <Text fontSize="xs" color="gray.500" fontWeight={600} minW="170px" textAlign="center">
              Minutes (min – max)
            </Text>
          </Flex>
          <Divider borderColor="gray.300" _dark={{ borderColor: 'gray.600' }} mb={1} />
          <VStack spacing={0} align="stretch">
            {thresholds.map((threshold, i) => (
              <Box key={`${threshold.cptCode}-${i}`}>
                {i > 0 && <Divider borderColor="gray.200" _dark={{ borderColor: 'gray.600' }} />}
                <IntrapartumThresholdRow threshold={threshold} index={i} />
              </Box>
            ))}
            {thresholds.length === 0 && (
              <Text fontSize="sm" color="gray.400" fontStyle="italic" py={2}>
                No intrapartum thresholds configured — click + Add Threshold.
              </Text>
            )}
          </VStack>
        </Box>
      </Expandable>

      <GenericEditModal<IntrapartumThreshold>
        field={addIntrapartumThresholdField}
        isOpen={addOpen}
        onClose={() => setAddOpen(false)}
        onSubmit={(d, _onProgress) => handleAddIntrapartumThreshold(d)}
        closeOnSubmit
      />
    </>
  )
}

// ─── §5.2: Delivery time thresholds section ───────────────────────────────────

const addDeliveryThresholdField: FieldMap = {
  name: 'Add Delivery Time Threshold',
  children: {
    cptCode: cptCodeField,
    label: thresholdLabelField,
    minMinutes: minMinutesField,
    maxMinutes: maxMinutesField,
  },
}

const DeliveryThresholdRow = ({
  threshold,
  index,
}: {
  threshold: DeliveryTimeThreshold
  index: number
}) => {
  const { handleUpdateDeliveryThreshold, handleDeleteDeliveryThreshold } =
    useTimeLogConfigAdminContext()

  return (
    <Flex align="center" gap={3} py={1.5} w="100%">
      <Box minW="90px">
        <Editable
          field={cptCodeField}
          value={threshold.cptCode}
          onSubmit={v => handleUpdateDeliveryThreshold(index, { ...threshold, cptCode: v })}
          dataCellProps={{ fontSize: 'sm', fontWeight: 600, py: 0 }}
        />
      </Box>
      <Box flex={1} minW={0}>
        <Editable
          field={thresholdLabelField}
          value={threshold.label}
          onSubmit={v => handleUpdateDeliveryThreshold(index, { ...threshold, label: v })}
          dataCellProps={{ fontSize: 'sm', py: 0 }}
        />
      </Box>
      <HStack spacing={1} flexShrink={0}>
        <NumberInput
          size="xs"
          w="72px"
          min={0}
          max={9999}
          value={threshold.minMinutes}
          onChange={val => {
            const n = Number(val)
            if (!isNaN(n)) handleUpdateDeliveryThreshold(index, { ...threshold, minMinutes: n })
          }}>
          <NumberInputField borderRadius={4} />
          <NumberInputStepper>
            <NumberIncrementStepper />
            <NumberDecrementStepper />
          </NumberInputStepper>
        </NumberInput>
        <Text fontSize="xs" color="gray.500">
          –
        </Text>
        <NumberInput
          size="xs"
          w="72px"
          min={0}
          max={9999}
          value={threshold.maxMinutes}
          onChange={val => {
            const n = Number(val)
            if (!isNaN(n)) handleUpdateDeliveryThreshold(index, { ...threshold, maxMinutes: n })
          }}>
          <NumberInputField borderRadius={4} />
          <NumberInputStepper>
            <NumberIncrementStepper />
            <NumberDecrementStepper />
          </NumberInputStepper>
        </NumberInput>
        <Text fontSize="xs" color="gray.500" whiteSpace="nowrap">
          min
        </Text>
      </HStack>
      <DeleteButton
        itemName={threshold.cptCode}
        onDelete={() => handleDeleteDeliveryThreshold(index)}
      />
    </Flex>
  )
}

const DeliveryTimeThresholdsSection = () => {
  const { data, handleAddDeliveryThreshold, loading } = useTimeLogConfigAdminContext()
  const [addOpen, setAddOpen] = useState(false)

  const thresholds = (data?.deliveryTimeThresholds ?? [])
    .slice()
    .sort((a, b) => a.minMinutes - b.minMinutes)

  return (
    <>
      <Expandable
        initExpanded
        header={() => (
          <Flex align="center" flex={1} minWidth={0}>
            <Flex direction="column" flex={1} px={4} py={2}>
              <Text fontSize="lg" fontWeight={600} color="gray.700" _dark={{ color: 'gray.200' }}>
                Delivery Time Thresholds
              </Text>
              <Text fontSize="xs" color="gray.500">
                AMA delivery code revisions expected September 2026 — update without a code deploy.
              </Text>
            </Flex>
            <Button
              size="sm"
              colorScheme="green"
              mr={2}
              isLoading={loading}
              onClick={e => {
                e.stopPropagation()
                setAddOpen(true)
              }}>
              + Add Threshold
            </Button>
          </Flex>
        )}
        headerProps={{ bg: 'gray.50', _dark: { bg: 'gray.800', borderColor: 'gray.700' } }}
        bg="white"
        _dark={{ bg: 'gray.900' }}>
        <Box px={3} py={2}>
          <Flex px={1} pb={1}>
            <Text fontSize="xs" color="gray.500" fontWeight={600} minW="90px">
              CPT Code
            </Text>
            <Text fontSize="xs" color="gray.500" fontWeight={600} flex={1}>
              Label
            </Text>
            <Text fontSize="xs" color="gray.500" fontWeight={600} minW="170px" textAlign="center">
              Minutes (min – max)
            </Text>
          </Flex>
          <Divider borderColor="gray.300" _dark={{ borderColor: 'gray.600' }} mb={1} />
          <VStack spacing={0} align="stretch">
            {thresholds.map((threshold, i) => (
              <Box key={`${threshold.cptCode}-${i}`}>
                {i > 0 && <Divider borderColor="gray.200" _dark={{ borderColor: 'gray.600' }} />}
                <DeliveryThresholdRow threshold={threshold} index={i} />
              </Box>
            ))}
            {thresholds.length === 0 && (
              <Text fontSize="sm" color="gray.400" fontStyle="italic" py={2}>
                No delivery thresholds configured — click + Add Threshold.
              </Text>
            )}
          </VStack>
        </Box>
      </Expandable>

      <GenericEditModal<DeliveryTimeThreshold>
        field={addDeliveryThresholdField}
        isOpen={addOpen}
        onClose={() => setAddOpen(false)}
        onSubmit={(d, _onProgress) => handleAddDeliveryThreshold(d)}
        closeOnSubmit
      />
    </>
  )
}

// ─── Main view ────────────────────────────────────────────────────────────────

const TimeLogConfigContent = () => (
  <VStack bg="gray.100" minH="100%" align="stretch" spacing={3} p={2}>
    <FieldLabelsSection />
    <EmThresholdsSection />
    <IntrapartumThresholdsSection />
    <DeliveryTimeThresholdsSection />
  </VStack>
)

export const TimeLogConfigAdminView = () => (
  <TimeLogConfigAdminProvider>
    <TimeLogConfigContent />
  </TimeLogConfigAdminProvider>
)
